<template>
  <div :class="`ranking-list mt-3  ${settings.type} ${listClasses}`">
    <div class="header d-flex">
      <div :class="`ranking-list__title__wrapper mt-2 pb-0 flex`">
        <div class="ranking-list__title d-inline-block">
          {{ title }}

          <popover
            v-if="customCols.includes('engagement')"
          >
            Engagement minutes is calculated as the total read time across emails & web views. Each read or view event is monitored to a maximum of 20 seconds only, in order to avoid overestimating engagement when email clients or browser windows are left open. Depending on the timeframe you are viewing, and the time of day you are viewing it, some of the data displayed here is delayed.
          </popover>
        </div>
      </div>

      <div
        v-if="isReady && settings.type === 'infinity-rank'"
        class="ml-auto"
      >
        <search
          v-model="searchQuery"
          placeholder="Search top results"
        />
      </div>
    </div>

    <div
      v-if="items && processedItems"
      class="ranking-list__wrapper mt-2 border-bottom"
    >
      <div class="ranking-list__header border-bottom font-weight-bold">
        <div class="col-fixed-small float-left" />

        <div class="row ranking-list__row">
          <div
            v-for="col in customCols"
            :key="col"
            :class="col === 'name' ? colNameClasses : colClasses(col)"
          >
            <span v-if="userDownloaded(col)">
              Downloaded
            </span>

            <span
              v-else
              class="ranking-list__header__content"
            >
              {{ colsMap[col].display }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="filteredItems && filteredItems.length === 0"
        class="ranking-list__empty"
      >
        No data
      </div>

      <div
        v-else
        class="ranking-list__item__wrapper"
      >
        <div
          v-for="(item, index) in processedItems"
          :key="index"
          class="ranking-list__item"
          :class="{active: settings.activeEnabled && (item.id || 0).toString() === (activeId || 1).toString(), 'not-found': item.notFound, 'd-none': !filteredItemIds.includes(item.id) }"
        >
          <div class="col-fixed-small float-left">
            <span class="ranking-list__item__change">
              <icon
                :icon="getIcon(item.change).icon"
                :class="getIcon(item.change).class"
              />
            </span>
            <span class="position-index">
              {{ startPosition + index }}
            </span>
          </div>

          <div class="row ranking-list__row">
            <div
              v-for="col in customCols"
              :key="col"
              :class="col === 'name' ? colNameClasses : colClasses(col)"
            >
              <router-link
                v-if="col === 'name' || (colsMap[col].path && $login.can(`${colsMap[col].path}.view`))"
                :to="getLinkTo(item, col)"
                :class="{ disabled: !settings.path || item.notFound || getLinkTo(item, col) === '#', }"
              >
                {{ item[col] }}
              </router-link>

              <span v-else>
                <span v-if="userDownloaded(col)">
                  <icon
                    :icon="parseInt(item[col]) > 0 ? 'tick' : 'stripe'"
                    :class="{positive: parseInt(item[col]) > 0, negative: parseInt(item[col]) <= 0}"
                  />
                </span>

                <span v-else>
                  {{ item[col] }}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="loading-wrapper"
    >
      <loading-icon
        :loading="!isReady"
        loading-class="text-center vertical-middle"
      />
    </div>
  </div>
</template>

<script>
import Search from '@/components/Search'

export default {
  components: {
    Search,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    processed: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: null,
    },
    activeId: {
      type: Number,
      default: null,
    },
    settings: {
      type: Object,
      required: true,
    },
    clientWidth: {
      type: Number,
      default: null,
    },
  },
  data () {
    return {
      processedItems: null,
      colsMap: {
        name: {display: 'Name', colSpanNarrow: 0, colSpanWide: 0, align: 'left'},
        engagement: {display: 'Web Views', colSpanNarrow: 3, colSpanWide: 2, align: 'right'},
        webViews: {display: 'Web Views', colSpanNarrow: 3, colSpanWide: 2, align: 'right'},
        views: {display: 'Web Views', colSpanNarrow: 3, colSpanWide: 2, align: 'right'},
        account: {display: 'Account', path: 'accounts', colSpanNarrow: 4, colSpanWide: 4, align: 'left'},
        channel: {display: 'Channel', path: 'channels', colSpanNarrow: 4, colSpanWide: 4, align: 'left'},
        analyst: {display: 'Analyst', path: 'analysts', colSpanNarrow: 4, colSpanWide: 4, align: 'left'},
        webDownloads: {display: 'Downloads', colSpanNarrow: 3, colSpanWide: 2, align: 'right'},
        emailViews: {display: 'Email Views', colSpanNarrow: 3, colSpanWide: 2, align: 'right'},
        sendgridOpened: {display: 'Email Opens', colSpanNarrow: 3, colSpanWide: 2, align: 'right'},
      },
      searchQuery: '',
      hideSmall: ['channel', 'analyst'],
      hideLarge: ['webDownloads'],
    }
  },
  mounted() {
    if (this.processed) {
      this.processedItems = this.items;
      console.log('set pi to i', this.processedItems, this.items)
    }
  },
  computed: {
    halfLen () {
      return Math.round(this.listLength / 2)
    },
    isReady () {
      return this.items != null && this.processedItems != null
    },
    listLength () {
      return this.items.length
    },
    customCols () {
      return this.settings.cols != null ? this.settings.cols : ['name', 'engagement']
    },
    startPosition () {
      return this.items && this.settings.activeEnabled ? this.items[0].position : 1
    },
    colNameClasses () {
      let colSpan = 12

      for (const col of this.customCols) {
        if (this.clientWidth !== null
          && !(this.clientWidth <= 992 && this.hideLarge.includes(col))
          && !(this.clientWidth <= 576 && this.hideSmall.includes(col))) {
          colSpan -= Math.floor(this.getColSpan(col)/this.colDivider)
        }
      }

      return `col-name col-${colSpan}`
    },
    listClasses () {
      return (this.customCols.length > 2 ? `ranking-list-table` : 'ranking-list-table')
    },
    colDivider () {
      return this.clientWidth <= 576
        ? 1
        : Math.floor(this.settings.colWidth/12) + 1
    },
    filteredItems () {
      console.log('bro',JSON.stringify(this.processedItems))
      return this.processedItems.filter((it) => (!this.searchQuery
        || (it.name && it.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
        || (it.email && it.email.toLowerCase().includes(this.searchQuery.toLowerCase()))
        || (it.account && it.account.toLowerCase().includes(this.searchQuery.toLowerCase()))
        || (it.analyst && it.analyst.toLowerCase().includes(this.searchQuery.toLowerCase()))
        || (it.channel && it.channel.toLowerCase().includes(this.searchQuery.toLowerCase()))))
    },
    filteredItemIds () {
      return this.filteredItems.map((it) => it.id)
    },
  },
  watch: {
    items (newData, oldData) {
      if (newData != null) {
        this.processTopList()
      }
    },
  },
  methods: {
    getColSpan (col) {
      return this.settings.colWidth > 6
        ? this.colsMap[col].colSpanWide
        : this.colsMap[col].colSpanNarrow
    },
    getIcon (change) {
      if (change < 0) {
        return {icon: 'chevron-down', class: 'negative'}
      } else if (change > 0) {
        return {icon: 'chevron-up', class: 'positive'}
      } else {
        return {icon: 'bullet', class: 'neutral'}
      }
    },
    processTopList () {

      const results = this.items.slice()

      results.map((item) => {
        item.engagement = item.value
      })

      this.processedItems = results
    },
    colClasses (col) {
      return `${col} col-${this.getColSpan(col)/this.colDivider} col-num text-${this.userDownloaded(col) ? 'center pr-0' : this.colsMap[col].align} ${this.hideLarge.includes(col) ? 'd-none d-lg-block' : ''} ${this.hideSmall.includes(col) ? 'd-none d-sm-block' : ''}`
    },
    getLinkTo (item, col) {
      return col === 'name'
        ? this.settings.path ? `/${this.settings.path}/${item.id}`: '#'
        : this.$login.can('analytics.channel') && col === 'channel'
          ? '/dashboard'
          : this.colsMap[col].path && item[col + '_id'] !== undefined && item[col + '_id'] !== null ? `/${this.colsMap[col].path}/${item[col + '_id']}` : '#'
    },
    userDownloaded (col) {
      return this.settings.path === 'users' && col === 'webDownloads'
    },
  },
}
</script>

<style lang="scss" scoped>
.ranking-list {
  min-width: 300px;
  overflow: hidden;
  &.infinity-rank {
    .ranking-list__item__wrapper {
      max-height: 550px;
      overflow: hidden;
      overflow-y: auto;
    }
  }
  &.ranking-list-table {
    .ranking-list__header {
      background: #FBFBFB;
      border: 1px solid #dfe2ee;
    }

    .ranking-list__item {
      border: 1px solid #dfe2ee;
      border-bottom: 0;
      // &:last-child {
      //   border-bottom: 1px solid #dfe2ee;
      // }
      &:first-child {
        border-top: none;
      }
    }
    .ranking-list__empty {
      border: 1px solid #dfe2ee;
    }
  }

  &__empty {
    padding: 13px;
    border-top: 0 !important;
    border-bottom: 0 !important;
  }

  &__title {
    margin: 0;
    padding-bottom: 6px;
    font-size: 16px;
    color: #000;
  }

  &__header {
    margin: 0;
    padding: 13px 0 13px 0;
    color: #1f2638;
  }
  &__item {
    margin: 0;
    padding: 13px 0;
    color: $black;
    border-bottom: 1px solid #dfe2ee;
    span {
      cursor: default;
    }
    &__change {
      svg.i.neutral {
        height: 10px;
      }
    }
    &:hover {
      background: $tellimer-hover;
    }
    &.active {
      background-color: $rankinglist-active;
      color: #1f2638;
      font-weight: bold;

      .position-index {
        font-weight: bold;
      }
      a {
        color: #1f2638;
      }
    }

    a {
      text-decoration: none;
      color: $tellimer-gray;
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }

      &.disabled {
        display: block;  /* For IE11/ MS Edge bug */
        pointer-events: none;
        cursor: inherit;
        &:hover {
          background: none;
        }
      }
    }
    &.not-found {
      opacity: 0.4;
    }
  }

  &__row {
    margin-left: 67px;
    margin-right: 0;
  }
  .col-fixed-small {
    width: 60px;
    margin-left: 13px
  }
  .col-name {
    padding-left: 5px;
  }
  .col-num {
    padding-left: 0;
    word-wrap: break-word;
  }
  .loading-wrapper {
    height: 470px;
    width: 100%;
  }
}
</style>
